import type { PlanData } from './types/plans';

/**
 * Static fallbacks for the legacy Premium / Business plans (the
 * pre-Essentials/Creator/Pro subscription model — grandfathered users
 * still resolve through these). Live pricing flows through the
 * `/admin/pricing` API via `usePricingOptions` / `useNewPricing`.
 *
 * Note: INR/BRL/AUD prices here mirror USD as placeholders and follow the
 * 1/100 minor-unit convention used by the legacy `formatCurrency` callers
 * for these plan shapes. Live INR pricing on this Stripe account is
 * stored in whole rupees and flows through the API path, not here, so
 * these placeholders never reach a real INR-charging code path.
 */
export const planData: { business: PlanData; premium: PlanData } = {
  business: {
    id: 15,
    sku: {
      monthly: 'UBBZSBMN01',
      yearly: 'UBBZSBAN01',
    },
    monthly: {
      USD: '18.99',
      CAD: '24.99',
      EUR: '17.99',
      GBP: '15.99',
      INR: '18.99',
      BRL: '18.99',
      AUD: '18.99',
    },
    monthly_price_in_pence: {
      USD: 1899,
      CAD: 2499,
      EUR: 1799,
      GBP: 1599,
      INR: 1899,
      BRL: 1899,
      AUD: 1899,
    },
    yearly: {
      USD: '179.88',
      CAD: '239.88',
      EUR: '167.88',
      GBP: '155.88',
      INR: '179.88',
      BRL: '179.88',
      AUD: '179.88',
    },
    yearly_price_in_pence: {
      USD: 17_988,
      CAD: 23_988,
      EUR: 16_788,
      GBP: 15_588,
      INR: 17_988,
      BRL: 17_988,
      AUD: 17_988,
    },
    monthly_equivalent: {
      USD: '14.99',
      CAD: '19.99',
      EUR: '13.99',
      GBP: '12.99',
      INR: '14.99',
      BRL: '14.99',
      AUD: '14.99',
    },
    monthly_equivalent_price_in_pence: {
      USD: 1499,
      CAD: 1999,
      EUR: 1399,
      GBP: 1299,
      INR: 1499,
      BRL: 1499,
      AUD: 1499,
    },
  },
  premium: {
    id: 1,
    sku: {
      monthly: 'UBPRSBMN01',
      yearly: 'UBPRSBAN01',
    },
    monthly: {
      USD: '6.99',
      CAD: '6.99',
      EUR: '6.99',
      GBP: '6.99',
      INR: '6.99',
      BRL: '6.99',
      AUD: '6.99',
    },
    monthly_price_in_pence: {
      USD: 699,
      CAD: 699,
      EUR: 699,
      GBP: 699,
      INR: 699,
      BRL: 699,
      AUD: 699,
    },
    yearly: {
      USD: '66.99',
      CAD: '66.99',
      EUR: '66.99',
      GBP: '66.99',
      INR: '66.99',
      BRL: '66.99',
      AUD: '66.99',
    },
    yearly_price_in_pence: {
      USD: 6699,
      CAD: 6699,
      EUR: 6699,
      GBP: 6699,
      INR: 6699,
      BRL: 6699,
      AUD: 6699,
    },
    monthly_equivalent: {
      USD: '5.59',
      CAD: '5.59',
      EUR: '5.59',
      GBP: '5.59',
      INR: '5.59',
      BRL: '5.59',
      AUD: '5.59',
    },
    monthly_equivalent_price_in_pence: {
      USD: 559,
      CAD: 559,
      EUR: 559,
      GBP: 559,
      INR: 559,
      BRL: 559,
      AUD: 559,
    },
  },
};

// For reference only during ABC testing
const newPlanData: { business: PlanData; premium: PlanData } = {
  business: planData.business,
  premium: {
    ...planData.premium,
    monthly: {
      USD: '7.99',
      CAD: '9.99',
      EUR: '7.99',
      GBP: '7.99',
      INR: '7.99',
      BRL: '7.99',
      AUD: '7.99',
    },
    monthly_price_in_pence: {
      USD: 799,
      CAD: 999,
      EUR: 799,
      GBP: 799,
      INR: 799,
      BRL: 799,
      AUD: 799,
    },
    yearly: {
      USD: '75.99',
      CAD: '95.99',
      EUR: '75.99',
      GBP: '75.99',
      INR: '75.99',
      BRL: '75.99',
      AUD: '75.99',
    },
    yearly_price_in_pence: {
      USD: 7599,
      CAD: 9599,
      EUR: 7599,
      GBP: 7599,
      INR: 7599,
      BRL: 7599,
      AUD: 7599,
    },
    monthly_equivalent: {
      USD: '6.33',
      CAD: '8.00',
      EUR: '6.33',
      GBP: '6.33',
      INR: '6.33',
      BRL: '6.33',
      AUD: '6.33',
    },
    monthly_equivalent_price_in_pence: {
      USD: 633,
      CAD: 800,
      EUR: 633,
      GBP: 633,
      INR: 633,
      BRL: 633,
      AUD: 633,
    },
  },
};

// For reference only during ABC testing
const newPlanDataUSD799 = {
  ...newPlanData,
  premium: {
    ...newPlanData.premium,
    id: 16,
    monthly: {
      ...newPlanData.premium.monthly,
      USD: '7.99',
    },
    monthly_price_in_pence: {
      ...newPlanData.premium.monthly_price_in_pence,
      USD: 799,
    },
    yearly: {
      ...newPlanData.premium.yearly,
      USD: '75.99',
    },
    yearly_price_in_pence: {
      ...newPlanData.premium.yearly_price_in_pence,
      USD: 7599,
    },
    monthly_equivalent: {
      ...newPlanData.premium.monthly_equivalent,
      USD: '6.33',
    },
    monthly_equivalent_price_in_pence: {
      ...newPlanData.premium.monthly_equivalent_price_in_pence,
      USD: 633,
    },
  },
};

// For reference only during ABC testing
const newPlanDataUSD899 = {
  ...newPlanData,
  premium: {
    ...newPlanData.premium,
    id: 17,
    monthly: {
      ...newPlanData.premium.monthly,
      USD: '8.99',
    },
    monthly_price_in_pence: {
      ...newPlanData.premium.monthly_price_in_pence,
      USD: 899,
    },
    yearly: {
      ...newPlanData.premium.yearly,
      USD: '85.99',
    },
    yearly_price_in_pence: {
      ...newPlanData.premium.yearly_price_in_pence,
      USD: 8599,
    },
    monthly_equivalent: {
      ...newPlanData.premium.monthly_equivalent,
      USD: '7.17',
    },
    monthly_equivalent_price_in_pence: {
      ...newPlanData.premium.monthly_equivalent_price_in_pence,
      USD: 717,
    },
  },
};
